<template>
  <!-- <div v-if="loading" class="custom-global-skeleton tw-w-full tw-h-full"></div> -->

  <div class="tw-flex tw-items-center tw-justify-between tw-pr-[23.5px]">
    <div
      class="tw-flex tw-items-center tw-leading-[130%] tw-w-full tw-text-[#211F58] tw-text-[15px] tw-font-semibold tw-not-italic"
    >
      {{ getComponentUiText("app-scanning-page-issue-over-time-title-1") }}
      <ButtonShowDuplicateAssessment
        :id-popover="'popover-issue-over-time-mini-graph-' + id"
        :classes="`tw-ml-[10px] tw-mb-[2px]`"
      >
        <div class="tw-p-[15px] tw-w-[247px]">
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="title-note">
              {{
                getComponentUiText("app-scanning-page-issue-over-time-title-1")
              }}
            </div>
          </div>
          <div
            class="tw-mt-[10px] tw-text-[11px] tw-font-[400] tw-text-[#878787] tw-whitespace-pre-line"
          >
            {{
              getComponentUiText(
                "app-scanning-page-mini-chart-note-description-1",
              )
            }}
          </div>
        </div>
      </ButtonShowDuplicateAssessment>
    </div>
    <select class="right-title tw-relative" @input="(e) => onFiltered(e)">
      <option
        v-for="(data, index) in dropdownData"
        :key="index"
        :value="data.value"
        class="tw-text-[#211F58] tw-text-center tw-text-[11px] tw-font-medium tw-leading-[8px]"
      >
        {{ data.name }}
      </option>
    </select>
  </div>

  <div class="tw-mt-[14px] tw-flex">
    <div class="tw-w-[161px]">
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-[6px]">
        <p
          class="tw-mb-[0px] tw-text-[13px] tw-leading-[130%] tw-font-medium tw-text-[#211F58]"
        >
          {{
            getComponentUiText(
              "app-scanning-page-mini-chart-resolved-last-run-1",
            )
          }}
        </p>
        <p
          class="tw-mb-[0px] tw-text-[13px] tw-leading-[130%] tw-font-normal tw-text-[#878787]"
        >
          {{ resolveLastRun }}
        </p>
      </div>
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-[6px]">
        <p
          class="tw-mb-[0px] tw-text-[13px] tw-leading-[130%] tw-font-medium tw-text-[#211F58]"
        >
          {{
            getComponentUiText(
              "app-scanning-page-mini-chart-resolved-velocity-1",
            )
          }}
        </p>
        <p
          class="tw-mb-[0px] tw-text-[13px] tw-leading-[130%] tw-font-normal tw-text-[#878787]"
        >
          {{ resolveVelocity }}
        </p>
      </div>
    </div>
    <div
      v-if="severity && severity.length > 1"
      class="tw-flex-[1] tw-relative tw-top-[-27px] tw-ml-[20px] tw-mr-[4px]"
    >
      <LineChart :height="90" :series="series" :chart-options="option" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getComponentUiText } from "@/utils/utilities";
import { ApexOptions } from "apexcharts";
import ButtonShowDuplicateAssessment from "../common/setting/buttonShowDuplicateAssessment.vue";
import { onMounted, PropType, Ref, ref, watch } from "vue";
import LineChart, { IChartSeries } from "../chart/lineChart.vue";
import { TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS } from "@/utils/constants";
import { getMarkers } from "@/utils/apexChart";
import { cloneDeep } from "lodash";
import { convertUTCTimeToLocalTime } from "@/utils/dateTimeUtils";

const props = defineProps({
  severity: {
    type: Array as PropType<Array<any>>,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
});

const filterSelected = ref(TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.Total);
const option: Ref<ApexOptions> = ref({});
const series: Ref<IChartSeries[]> = ref([]);
const resolveLastRun: Ref<string> = ref("");
const resolveVelocity: Ref<string> = ref("");

const dropdownData = [
  {
    name: getComponentUiText(
      "app-component-assessments-assessment-question-total-score-1",
    ),
    value: TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.Total,
  },
  {
    name: getComponentUiText(
      "app-component-assessments-checklists-exposures-card-2",
    ),
    value: TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.Critical,
  },
  {
    name: getComponentUiText("app-assess-recommendations-high-risk-1"),
    value: TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.High,
  },
  {
    name: getComponentUiText("app-assess-recommendations-medium-risk-1"),
    value: TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.Medium,
  },
  {
    name: getComponentUiText("app-assess-recommendations-low-risk-1"),
    value: TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.Low,
  },
];

onMounted(() => {
  onProceedData();
});

watch(
  () => props.severity,
  () => {
    onProceedData(filterSelected.value);
  },
);

const onProceedData = (
  type = TYPE_ISSUE_OVER_TIME_MINI_GRAPH_OPTIONS.Total,
) => {
  const dataArray = props.severity;
  const filterData = dataArray
    .sort((a: any, b: any) => a.startedAt - b.startedAt)
    .map((item) => {
      return item[type];
    });
  if (!props.severity || props.severity.length <= 1) {
    resolveLastRun.value = "N/A";
    resolveVelocity.value = "N/A";
    return;
  }
  resolveLastRun.value = `${
    dataArray[dataArray.length - 2][type] -
    dataArray[dataArray.length - 1][type]
  }`;
  resolveVelocity.value = Number(
    (dataArray[0][type] - dataArray[dataArray.length - 1][type]) /
      (dataArray.length - 1),
  ).toFixed(2);
  let markers = getMarkers(cloneDeep(filterData), cloneDeep(filterData), true);
  markers = markers.map((item) => {
    return {
      ...item,
      size: 0,
    };
  });

  const maxScore = Math.max(...dataArray);
  const minScore = Math.min(...dataArray);

  const fiftyPercentile = (maxScore + minScore) * 0.5;

  series.value = [
    {
      name: getComponentUiText("app-scan-scanning-issues-number-block-1"),
      data: filterData,
    },
  ];

  option.value = {
    stroke: { width: 3, curve: "straight" },
    chart: { toolbar: { show: false } },
    tooltip: {
      x: {
        formatter: function (value) {
          return convertUTCTimeToLocalTime(
            dataArray[value - 1].startedAt,
            undefined,
            "HH:mm",
          );
        },
      },
      y: {
        formatter: function (value) {
          return Number(value).toFixed(0);
        },
      },
    },
    yaxis: {
      show: false,
    },
    xaxis: {
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
      crosshairs: {
        show: false,
      },
    },

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        type: "vertical",
        inverseColors: true,
        colorStops: [
          {
            offset: 0,
            color: "#F70000",
            opacity: 1,
          },
          {
            offset: fiftyPercentile,
            color: "#FFA300",
            opacity: 1,
          },
          {
            offset: 100,
            color: "#01893D",
            opacity: 1,
          },
        ],
      },
    },
    grid: {
      show: false,
      padding: { left: 10, right: 20, top: 0, bottom: 10 },
    },
  };
};

const onFiltered = (e) => {
  onProceedData(e.target.value);
};
</script>

<style scoped>
.title-note {
  color: var(--Brand-Navy, #211f58);
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}
.right-title {
  background: #e9e9ee;
  width: 120px;
  color: #211f58;
  font-weight: 500;
  cursor: pointer;
  font-size: 11px;
  height: 22px;
  border-radius: 100px;
  padding: 0 20px 0 10px;
  border: none;

  appearance: none;
  background-image: url("../../assets/img/arrow_down3.svg");
  background-repeat: no-repeat;
  background-position: right 0.4rem top 50%;
  background-size: 0.55rem auto;
}
select:focus-visible {
  outline: none;
}
</style>
